import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import client from '../api/client';
import { loginRequest } from '../config/msalConfig';
import { appInsights } from '../lib/appInsights';

function setAiUser(u) {
  if (!appInsights) return;
  if (!u) {
    appInsights.clearAuthenticatedUserContext();
    return;
  }
  // Sanitize: AI rejects authenticatedUserId with commas, semicolons, equals, spaces.
  const safeId = String(u.id).replace(/[,;= ]/g, '_');
  appInsights.setAuthenticatedUserContext(safeId, u.email, true);
}

const AuthContext = createContext(null);

/**
 * Normalize user object to always have `roles` array and backward-compat `role`.
 * Handles both old (role: 'owner') and new (roles: ['admin','supervisor']) JWT payloads.
 */
function normalizeUser(u) {
  if (!u) return u;

  let roles = u.roles || [];
  if (roles.length === 0 && u.role) {
    if (u.role === 'owner') {
      roles = ['admin', 'supervisor', 'employee'];
    } else {
      roles = [u.role];
    }
  }

  const role = u.role || (roles.includes('admin') ? 'admin' : (roles.includes('reviewer') ? 'reviewer' : (roles.includes('supervisor') ? 'supervisor' : 'employee')));
  return { ...u, roles, role };
}

export function AuthProvider({ children }) {
  const { instance: msalInstance, accounts } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const ssoAttempted = useRef(false);

  const [ssoError, setSsoError] = useState(null);

  // Check existing JWT on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    client.get('/api/auth/me')
      .then((r) => {
        const u = normalizeUser(r.data);
        setUser(u);
        setAiUser(u);
      })
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, []);

  // When MSAL detects an authenticated account (after redirect), exchange for app JWT
  useEffect(() => {
    if (!msalAuthenticated || accounts.length === 0) return;
    if (localStorage.getItem('token') || ssoAttempted.current) return;
    ssoAttempted.current = true;

    (async () => {
      try {
        const silentResult = await msalInstance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });
        if (silentResult?.idToken) {
          const res = await client.post('/api/auth/sso', { idToken: silentResult.idToken });
          localStorage.setItem('token', res.data.token);
          const u = normalizeUser(res.data.user);
          setUser(u);
          setAiUser(u);
        }
      } catch (err) {
        console.error('SSO auto-login error:', err);
        setSsoError(err.response?.data?.error || err.message || 'Error al autenticar con el servidor.');
      }
    })();
  }, [msalAuthenticated, accounts]);

  /** SSO login — triggers Microsoft redirect */
  function loginWithSSO() {
    msalInstance.loginRedirect(loginRequest);
  }

  /** Password login — fallback for accounts not in the Azure AD tenant */
  async function login(email, password) {
    const res = await client.post('/api/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    const u = normalizeUser(res.data.user);
    setUser(u);
    setAiUser(u);
    return u;
  }

  function logout() {
    localStorage.removeItem('token');
    setUser(null);
    setAiUser(null);
    // Also sign out of Microsoft session if active
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.logoutRedirect({ postLogoutRedirectUri: '/login' }).catch(() => {});
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithSSO, logout, ssoError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Utility: check if user has a specific role */
export function userHasRole(user, role) {
  return user?.roles?.includes(role) ?? false;
}

export function userIsAdmin(user) {
  return userHasRole(user, 'admin');
}

export function userIsSupervisor(user) {
  return userHasRole(user, 'supervisor') || userHasRole(user, 'admin');
}

export function userIsReviewer(user) {
  return userHasRole(user, 'reviewer') || userHasRole(user, 'admin');
}

export function userIsEngineer(user) {
  return userHasRole(user, 'engineer') || userHasRole(user, 'admin');
}

export function userIsManager(user) {
  return userHasRole(user, 'manager') || userHasRole(user, 'admin');
}

export function userIsAccountant(user) {
  return userHasRole(user, 'accountant') || userHasRole(user, 'admin');
}

/** @deprecated — kept for backward compat, always returns false now */
export function userIsAccounting(user) {
  return false;
}

/** @deprecated — kept for backward compat, always returns false now */
export function userIsPtoApprover(user) {
  return false;
}
